import type { InvoiceBillingEventRow } from '../../billing/services/billingApi';
import type {
    TitleDebtHistory,
    TitleDebtHistoryBillingEvent,
    TitleDebtHistoryInvoice,
} from '../../settlement/services/settlementApi';
import { invoiceLabel, type BadgeView } from './collectionHistory';

export interface InvoiceBillingEventGroup {
    invoiceId: string;
    label: string;
    status: string;
    events: TitleDebtHistoryBillingEvent[];
}

const INVOICE_STATUS: Record<string, BadgeView> = {
    OPEN: { text: 'Em aberto', color: 'gold' },
    PARTIALLY_PAID: { text: 'Parcial', color: 'olive' },
    PAID: { text: 'Paga', color: 'green' },
    SUPERSEDED: { text: 'Renegociada', color: 'gray' },
    CANCELLED: { text: 'Cancelada', color: 'red' },
};

export const mapInvoiceBillingEvent = (row: InvoiceBillingEventRow): TitleDebtHistoryBillingEvent => ({
    billing_event_id: String(row.id),
    billing_event_ref: row.billingEventRef,
    billing_event_name: row.billingEventName,
    status: row.status,
    created_by: row.createdBy,
    created_by_name: row.createdByName,
    finished_by: row.finishedBy,
    finished_by_name: row.finishedByName,
    completed_at: row.completedAt,
    event_date_limit: row.eventDateLimit,
    notes: row.notesList,
    created_at: row.createdAt,
});

export const shownBillingEventIds = (history: TitleDebtHistory): Set<string> => {
    const ids = history.movements
        .flatMap((movement) => movement.invoice?.billing_events ?? [])
        .map((event) => String(event.billing_event_id));
    return new Set(ids);
};

export const invoicesOfCharge = (
    invoices: TitleDebtHistoryInvoice[],
    chargeId: string | null,
): TitleDebtHistoryInvoice[] => {
    if (!chargeId) return [];
    return invoices.filter((invoice) => invoice.charge_id === chargeId);
};

export const invoiceBillingStatusBadge = (status: string | null): BadgeView => {
    if (!status) return { text: '—', color: 'gray' };
    return INVOICE_STATUS[status] || { text: status, color: 'gray' };
};

export const buildInvoiceGroup = (
    invoice: TitleDebtHistoryInvoice,
    rows: InvoiceBillingEventRow[],
    shownIds: Set<string>,
): InvoiceBillingEventGroup | null => {
    const events = rows
        .map(mapInvoiceBillingEvent)
        .filter((event) => !shownIds.has(event.billing_event_id));
    if (events.length === 0) return null;
    return {
        invoiceId: invoice.invoice_id,
        label: invoiceLabel(invoice),
        status: invoice.status ?? '',
        events,
    };
};
